import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTeam } from "../../context/TeamContext";
import { useAccess } from "../../context/AccessContext";
import { useTeamRecord } from "../../context/useTeamRecord";
import { hasPersonalFeature, hasTeamFeature, resolveTeamEntitlements } from "../../utils/entitlements";
import { getRequestedTeamId } from "../../utils/teamRouteTarget";
import LoginTeamGate from "../core-components/LoginTeamGate";
import Footer from "../core-components/Footer";
import AccessStatusPage from "./AccessStatusPage";
import "../core-components/LoginTeamGate.css";

export const defaultAccessFooter = () => !(typeof window !== "undefined" && window.desktopAPI);

function AccessBlockedPage({
  title, description = "", buttonText = "Go to Team Billing",
  buttonPath = "/team-setup-billing", showFooter = defaultAccessFooter(),
}) {
  const navigate = useNavigate();
  return <>
    <div className="LoginTeamGatePage"><div className="LoginTeamGateContent"><div className="LoginTeamGateCard">
      <h2 className="LoginTeamGateTitle">{title}</h2>
      {description ? <p>{description}</p> : null}
      <button type="button" className="LoginTeamGateButton" onClick={() => navigate(buttonPath)}>{buttonText}</button>
    </div></div></div>
    {showFooter && <Footer />}
  </>;
}

function hasResolvedTeamFeature(entitlements, subscription, featureKey) {
  if (entitlements?.features && Object.prototype.hasOwnProperty.call(entitlements.features, featureKey)) {
    return hasTeamFeature(entitlements, featureKey);
  }
  return hasTeamFeature(resolveTeamEntitlements(subscription), featureKey);
}

function useRouteAccessState({ needsEntitlements = false, allowPendingBillingAccess = false, allowPendingTeamAccess = false } = {}) {
  const auth = useAuth();
  const team = useTeam();
  const access = useAccess() || {};
  const location = useLocation();
  const resolvedTeamId = getRequestedTeamId(location.search) || team.teamId || null;
  // Billing deep links can target a different team without switching the workspace.
  const otherTeam = useTeamRecord(
    resolvedTeamId !== team.teamId ? auth.userData?.uid : null, resolvedTeamId
  );
  const membership = resolvedTeamId !== team.teamId ? otherTeam : team;
  const isActiveTeamMember = Boolean(auth.userData && resolvedTeamId && membership.isActiveTeamMember);
  const pendingJoinAccess = access.pendingJoinAccess?.teamId === resolvedTeamId ? access.pendingJoinAccess : null;
  const canAccessPendingBilling = pendingJoinAccess?.canAccessBillingApproval === true;
  const allowedPending = (allowPendingBillingAccess && canAccessPendingBilling) ||
    (allowPendingTeamAccess && pendingJoinAccess?.active === true);
  const needsAccess = (needsEntitlements && isActiveTeamMember) ||
    ((allowPendingBillingAccess || allowPendingTeamAccess) && !isActiveTeamMember && Boolean(resolvedTeamId));
  const matchingEntitlements = access.activeTeamId === resolvedTeamId && !access.teamError;
  const targetError = needsEntitlements && isActiveTeamMember && !access.loading && !access.error && !matchingEntitlements
    ? Object.assign(new Error("Active team does not match this page."), { code: "access/team-mismatch" }) : null;
  const error = auth.error ||
    (!allowedPending && membership.error) ||
    (needsAccess && access.error) || targetError || null;
  const isLoading = !error && Boolean(auth.loading ||
    (auth.userData && membership.loading && !allowedPending) || (needsAccess && access.loading));
  const refreshAccess = () => {
    if (auth.error) return auth.refreshAuth?.();
    if (membership.error) return membership.refreshTeam?.();
    return access.refreshAccess?.();
  };
  return {
    userData: auth.userData, resolvedTeamId, isActiveTeamMember,
    accountLoading: auth.loading, accountError: auth.error,
    resolvedTeamName: membership.teamName || membership.team?.teamName || membership.team?.name || null,
    pendingJoinAccess, canAccessPendingBilling, isLoading, error, refreshAccess,
    activeTeamSubscription: matchingEntitlements && !access.error && !access.loading ? access.activeTeamSubscription : null,
    activeTeamEntitlements: matchingEntitlements && !access.error && !access.loading ? access.activeTeamEntitlements : null,
  };
}

// Membership-only pages must not depend on subscription resolution.
export function useTeamMembershipAccess() {
  return useRouteAccessState();
}

export function useTeamDashboardAccess() {
  return useRouteAccessState({ allowPendingTeamAccess: true });
}

export function useTeamFeatureAccess(featureKey) {
  const state = useRouteAccessState({ needsEntitlements: true });
  return {
    ...state,
    hasFeature: !state.isLoading && !state.error && state.isActiveTeamMember &&
      hasResolvedTeamFeature(state.activeTeamEntitlements, state.activeTeamSubscription, featureKey),
  };
}

export function RequireAuthPage({ children, loginMessage = "Please Login to access this page.", showFooter = defaultAccessFooter() }) {
  // Account-only features must never wait on team membership or billing.
  const { userData, loading, error, refreshAuth } = useAuth();
  if (loading || error) return <AccessStatusPage error={error} onRetry={refreshAuth} showFooter={showFooter} />;
  if (!userData) return <LoginTeamGate requireTeam={false} loginMessage={loginMessage} showFooter={showFooter} />;
  return children;
}

function MembershipDenied({ state, showFooter }) {
  if (!state.userData) return <LoginTeamGate requireTeam={false} showFooter={showFooter} />;
  if (state.pendingJoinAccess?.active) return <AccessBlockedPage
    title="This page is limited while your team join is still pending."
    description="You can still open the Team Dashboard and any billing steps available to you."
    buttonText="Back to Team Dashboard" buttonPath="/team-center/dashboard" showFooter={showFooter}
  />;
  return <AccessBlockedPage
    title="Join or create a team to access this page."
    description="This page requires active membership in the selected team."
    buttonText="Go to Team Dashboard" buttonPath="/team-center/dashboard" showFooter={showFooter}
  />;
}

export function RequireTeamMembershipPage({ children, allowPendingBillingAccess = false, showFooter = defaultAccessFooter() }) {
  const state = useRouteAccessState({ allowPendingBillingAccess });
  if (state.isLoading || state.error) return <AccessStatusPage error={state.error} onRetry={state.refreshAccess} showFooter={showFooter} />;
  if (state.isActiveTeamMember || (allowPendingBillingAccess && state.canAccessPendingBilling)) return children;
  return <MembershipDenied state={state} showFooter={showFooter} />;
}

export function RequireTeamFeaturePage({
  featureKey, children, title = "This feature is not included in your active team plan.",
  description = "Review your team's subscription if you need this page.",
  buttonText = "Review Team Subscription", buttonPath = "/team-setup-billing", showFooter = defaultAccessFooter(),
}) {
  const state = useTeamFeatureAccess(featureKey);
  if (state.isLoading || state.error) return <AccessStatusPage error={state.error} onRetry={state.refreshAccess} showFooter={showFooter} />;
  if (!state.isActiveTeamMember) return <MembershipDenied state={state} showFooter={showFooter} />;
  if (!state.hasFeature) return <AccessBlockedPage {...{title, description, buttonText, buttonPath, showFooter}} />;
  return children;
}

export function RequirePersonalFeaturePage({
  featureKey, children, title = "This feature is not available on your current access level.",
  description = "Join a paid team or review your billing setup to unlock this page.",
  buttonText = "Open Billing", buttonPath = "/billing", showFooter = defaultAccessFooter(),
}) {
  const auth = useAuth();
  const access = useAccess() || {};
  const error = auth.error || (auth.userData && access.error);
  if (auth.loading || (auth.userData && access.loading) || error) {
    return <AccessStatusPage error={error} onRetry={auth.error ? auth.refreshAuth : access.refreshAccess} showFooter={showFooter} />;
  }
  if (!auth.userData) return <LoginTeamGate requireTeam={false} showFooter={showFooter} />;
  // This is an account capability across eligible teams, independent of the selected team.
  if (!hasPersonalFeature(access.personalEntitlements, featureKey)) {
    return <AccessBlockedPage {...{title, description, buttonText, buttonPath, showFooter}} />;
  }
  return children;
}
